import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api, blockApi, type BlockedUser } from '../api/client';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import type { Delegate } from '../types';

export function Settings() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [delegateError, setDelegateError] = useState('');
  const [delegateLoading, setDelegateLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  useEffect(() => {
    blockApi.list().then(setBlockedUsers).catch(() => {});
    api.get('/account/delegates').then(({ data }) => setDelegates(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  async function handleMfaSetup() {
    setMfaLoading(true);
    try {
      navigate('/mfa-setup');
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleDisableMfa(e: { preventDefault(): void }) {
    e.preventDefault();
    setDisableError('');
    setDisableLoading(true);
    try {
      await api.post('/auth/mfa/disable', { code: disableCode });
      await refresh();
      setShowDisableMfa(false);
      setDisableCode('');
    } catch (err: unknown) {
      setDisableError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to disable MFA');
    } finally {
      setDisableLoading(false);
    }
  }

  async function handleChangePassword(e: { preventDefault(): void }) {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');

    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match.');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setChangePasswordSuccess('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowChangePassword(false);
      setTimeout(() => setChangePasswordSuccess(''), 4000);
    } catch (err: unknown) {
      setChangePasswordError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to change password');
    } finally {
      setChangePasswordLoading(false);
    }
  }

  async function handleUnblock(userId: string) {
    setUnblockingId(userId);
    try {
      await blockApi.unblock(userId);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
    } finally {
      setUnblockingId(null);
    }
  }

  async function handleAccountDeleted() {
    await logout();
    navigate('/login');
  }

  async function handleAddDelegate(e: FormEvent) {
    e.preventDefault();
    if (!delegateEmail.trim()) return;
    setDelegateError('');
    setDelegateLoading(true);
    try {
      const { data } = await api.post('/account/delegates', { email: delegateEmail.trim() });
      setDelegates(prev => [...prev, data]);
      setDelegateEmail('');
    } catch (err: unknown) {
      setDelegateError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to add delegate');
    } finally {
      setDelegateLoading(false);
    }
  }

  async function handleRemoveDelegate(id: string) {
    if (!confirm('Remove this delegate?')) return;
    await api.delete(`/account/delegates/${id}`);
    setDelegates(prev => prev.filter(d => d.id !== id));
  }

  async function handleToggleNotify() {
    if (!user) return;
    setNotifyLoading(true);
    try {
      await api.patch('/auth/me', { notifyOnCircleActivity: !user.notifyOnCircleActivity });
      await refresh();
    } finally {
      setNotifyLoading(false);
    }
  }

  if (!user) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-serif font-bold text-stone-800">Settings</h1>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <h2 className="text-sm font-semibold text-stone-700 mb-1">Account</h2>
        <p className="text-stone-600">{user.name}</p>
        <p className="text-stone-400 text-sm">{user.email}</p>
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-stone-700 mb-1">Circle Activity Emails</h2>
            <p className="text-stone-500 text-sm">
              Get an email when someone in your circle creates a new event or adds photos.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleNotify}
            disabled={notifyLoading}
            aria-pressed={user.notifyOnCircleActivity}
            aria-label="Toggle circle activity emails"
            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${user.notifyOnCircleActivity ? 'bg-indigo-600' : 'bg-stone-200'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${user.notifyOnCircleActivity ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <h2 className="text-sm font-semibold text-stone-700 mb-1">Password</h2>
        {!showChangePassword ? (
          <>
            <p className="text-stone-500 text-sm mb-3">Change your account password.</p>
            <button
              onClick={() => { setShowChangePassword(true); setChangePasswordError(''); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              Change Password
            </button>
            {changePasswordSuccess && <p className="text-green-700 text-sm mt-2 font-medium">{changePasswordSuccess}</p>}
          </>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Current Password</label>
              <input
                type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                required autoFocus
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">New Password</label>
              <input
                type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                required minLength={8}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Confirm New Password</label>
              <input
                type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)}
                required minLength={8}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {changePasswordError && <p className="text-red-600 text-sm">{changePasswordError}</p>}
            <div className="flex gap-2">
              <button
                type="submit" disabled={changePasswordLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                {changePasswordLoading ? 'Saving…' : 'Save Password'}
              </button>
              <button
                type="button"
                onClick={() => { setShowChangePassword(false); setChangePasswordError(''); setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword(''); }}
                className="px-4 py-2 bg-white border border-stone-200 text-stone-600 rounded-lg text-sm hover:bg-stone-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <h2 className="text-sm font-semibold text-stone-700 mb-1">Two-Factor Authentication</h2>
        <p className="text-stone-500 text-sm mb-3">
          {user.mfaEnabled ? 'MFA is enabled on your account.' : 'Add an extra layer of security.'}
        </p>
        {!user.mfaEnabled && (
          <button
            onClick={handleMfaSetup} disabled={mfaLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            Set Up MFA
          </button>
        )}
        {user.mfaEnabled && !showDisableMfa && (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 text-green-700 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Enabled
            </span>
            <button
              onClick={() => { setShowDisableMfa(true); setDisableError(''); setDisableCode(''); }}
              className="text-xs text-red-600 hover:underline"
            >
              Disable MFA
            </button>
          </div>
        )}
        {user.mfaEnabled && showDisableMfa && (
          <form onSubmit={handleDisableMfa} className="space-y-2">
            <label className="block text-sm text-stone-600">
              Enter your current authenticator code to disable MFA
            </label>
            <div className="flex gap-2">
              <input
                type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)}
                required autoFocus maxLength={6}
                placeholder="6-digit code"
                className="w-32 border border-stone-300 rounded-lg px-3 py-2 text-sm tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                type="submit" disabled={disableLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {disableLoading ? 'Disabling…' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={() => { setShowDisableMfa(false); setDisableError(''); setDisableCode(''); }}
                className="px-4 py-2 bg-white border border-stone-200 text-stone-600 rounded-lg text-sm hover:bg-stone-50"
              >
                Cancel
              </button>
            </div>
            {disableError && <p className="text-red-600 text-sm">{disableError}</p>}
          </form>
        )}
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <h2 className="text-sm font-semibold text-stone-700 mb-1">Blocked Users</h2>
        {blockedUsers.length === 0 ? (
          <p className="text-stone-500 text-sm">You haven't blocked anyone.</p>
        ) : (
          <ul className="space-y-2">
            {blockedUsers.map(b => (
              <li key={b.id} className="flex items-center justify-between text-sm">
                <span className="text-stone-600">{b.name}</span>
                <button
                  onClick={() => handleUnblock(b.id)} disabled={unblockingId === b.id}
                  className="text-indigo-600 hover:underline text-xs disabled:opacity-50"
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-stone-100">
        <h2 className="text-sm font-semibold text-stone-700 mb-1">Delegates</h2>
        <p className="text-stone-500 text-sm mb-3">
          Give someone you trust — like a child helping an elderly parent — full access to view and edit your archive.
        </p>
        <form onSubmit={handleAddDelegate} className="flex gap-3 mb-2">
          <input
            type="email"
            value={delegateEmail}
            onChange={e => setDelegateEmail(e.target.value)}
            placeholder="their@email.com"
            required
            className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="submit"
            disabled={delegateLoading || !delegateEmail.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {delegateLoading ? 'Adding…' : 'Add Delegate'}
          </button>
        </form>
        {delegateError && <p className="text-red-600 text-sm mb-2">{delegateError}</p>}
        <p className="text-xs text-stone-400 mb-3">
          The person must have an EventsCarousel account. They can then switch to your archive from their account switcher.
        </p>

        {delegates.length === 0 ? (
          <p className="text-stone-400 text-sm">No delegates yet.</p>
        ) : (
          <ul className="space-y-2">
            {delegates.map(d => (
              <li key={d.id} className="flex items-center justify-between text-sm border border-stone-100 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-semibold">
                    {(d.delegate.name?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div>
                    <p className="text-stone-700 font-medium">{d.delegate.name}</p>
                    <p className="text-xs text-stone-400">{d.delegate.email || 'No email'}</p>
                  </div>
                </div>
                <button onClick={() => handleRemoveDelegate(d.id)} className="text-xs text-red-600 hover:underline">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-red-100">
        <h2 className="text-sm font-semibold text-red-600 mb-1">Danger Zone</h2>
        <p className="text-stone-500 text-sm mb-3">
          Permanently delete your account and, if you own a family archive, everything in it.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50"
        >
          Delete Account
        </button>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          userEmail={user.email}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleAccountDeleted}
        />
      )}
    </div>
  );
}
